import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { catchError, of } from 'rxjs';
import { DATA_SELECTOR_INJECTION_TOKEN, Logger, SharedModule } from '@aiwyn/shared';
import { FilePreviewService } from '@aiwyn/services';
import { CxpEsignApiService } from '../../services/cxp-esign-api.service';
import { CxpRouteParamsService } from '../../services/cxp-route-params.service';
import { CxpCrlDetailSelectors } from '../../state/cxp-crl-detail-state.selectors';
import { LoadRequestDetail, SetSelectedRequestId } from '../../state/cxp-crl-detail.actions';
import { parseRecipientId } from '../../shared';
import {
  CxpEsignDetail,
  CXP_ROUTE_TOKEN_ESIGN_DETAIL,
  CXP_ROUTE_TOKEN_ESIGN_SIGN,
  RECIPIENT_ID_QUERY_PARAM,
} from '../../models';
import { ROUTE_TOKEN_CXP_TASKS, ROUTE_TOKEN_MAIN_BASE } from '../../../../routing-constants';
import { DeliverableDetailHeaderComponent } from '../deliverable-detail-container/components/deliverable-detail-header/deliverable-detail-header.component';
import { CrlRightRailComponent } from '../cxp-new-crl-page/components/crl-right-rail/crl-right-rail.component';
import { EsignSignatureBlockComponent } from './components/esign-signature-block/esign-signature-block.component';

const DEFAULT_SIGNED_FILENAME = 'Signed document.pdf';

const GENERIC_UNAVAILABLE_MESSAGE = 'We couldn\'t load the signature details for this request. Please refresh and try again.';

/**
 * A rejection a 4xx wrote for this caller is worth showing verbatim: a link that has been revoked,
 * a bundle no longer awaiting signatures, a signature already given. Each names a state the signer
 * can act on, which the generic copy has no way to say. A 5xx body can carry internals, so those
 * keep the generic text.
 */
export function clientFacingMessage(error: unknown): string | null {
  const response = error as HttpErrorResponse;
  const detail = response?.error?.message;
  const isRejection = response?.status >= 400 && response?.status < 500;
  return typeof detail === 'string' && detail.trim() && isRejection ? detail : null;
}

/**
 * eSign task detail page (Figma 2.0 / 2.1 / 3.0 / 3.1).
 *
 * Owns the `esign/:requestId` route. An eSign request is a shell with no items, so the
 * CRL page's list, progress tiles and filters would all render empty; the signature block
 * takes their place while the header and rail stay the ones the CRL page already uses.
 * Because the two pages are that different, the kind of request lives in the path — an
 * eSign link resolves here without anything having to look the request up first.
 *
 * Two reads back the page. The request itself comes from the same CRL detail
 * state the redesigned page loads, which is what feeds the header and the rail.
 * The bundle behind it comes from the eSign API, and is the only part that can
 * be absent on an otherwise healthy page — hence its own empty state rather
 * than an error for the whole view.
 */
@Component({
  selector: 'cxp-esign-detail-container',
  templateUrl: './esign-detail-container.component.html',
  styleUrls: ['./esign-detail-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SharedModule,
    MatProgressBarModule,
    DeliverableDetailHeaderComponent,
    CrlRightRailComponent,
    EsignSignatureBlockComponent,
  ],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'cxp-esign-detail',
    },
  ],
})
export class EsignDetailContainerComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly routeParams = inject(CxpRouteParamsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly esignApi = inject(CxpEsignApiService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);
  private readonly filePreview = inject(FilePreviewService);

  protected readonly request = this.store.selectSignal(CxpCrlDetailSelectors.getSelectedRequestDisplay);
  private readonly detailData = this.store.selectSignal(CxpCrlDetailSelectors.getDetailData);

  protected readonly esign = signal<CxpEsignDetail | null>(null);
  /** True once the eSign read has settled without producing a bundle. */
  protected readonly esignUnavailable = signal(false);
  private readonly esignError = signal<string | null>(null);
  protected readonly esignUnavailableMessage = computed(() => this.esignError() ?? GENERIC_UNAVAILABLE_MESSAGE);

  protected readonly serviceLineName = computed(() => this.detailData()?.serviceLine?.name ?? null);

  /**
   * The Figma titles the page `E-Sign: {signer}, {name}` while the breadcrumb keeps the plain name.
   *
   * The signer leads, as it does on the task card: a request owing the same person two signatures
   * opens two pages whose document, client and description are identical, so without the name there
   * is nothing on the page saying which signature is about to be signed. It arrives with the eSign
   * read rather than the request, so the title reads document-only until that settles.
   */
  protected readonly pageTitle = computed(() => {
    const name = this.request().name;
    if (!name) return null;
    const signer = this.esign()?.signerDisplayName;
    return signer ? `E-Sign: ${signer}, ${name}` : `E-Sign: ${name}`;
  });

  protected readonly isLoading = computed(() => this.request().isLoading);

  /**
   * Which of the caller's signatures this page is about, when the request owes them more than one.
   * Read from the URL rather than passed down, so a deep link to a specific signature keeps working.
   */
  private readonly recipientId = computed(() =>
    parseRecipientId(this.route.snapshot.queryParamMap.get(RECIPIENT_ID_QUERY_PARAM)),
  );

  ngOnInit(): void {
    const requestId = this.routeParams.requestId();
    if (requestId == null) return;

    this.store.dispatch([new SetSelectedRequestId(requestId), new LoadRequestDetail(requestId)]);
    this.loadEsignDetail(requestId);
  }

  /**
   * A child route rather than an overlay, so Back returns to this page and the
   * signer's URL still names the request they came from.
   *
   * Which signature carries over: a request owing the same person two of them cannot be told apart
   * by the session, and dropping the id here would hand the signing surface the ambiguity this page
   * had already resolved.
   */
  protected onReviewAndSign(): void {
    const requestId = this.routeParams.requestId();
    if (requestId == null) return;
    const recipientId = this.recipientId();
    void this.router.navigate(
      [`/${ROUTE_TOKEN_MAIN_BASE}`, ROUTE_TOKEN_CXP_TASKS, CXP_ROUTE_TOKEN_ESIGN_DETAIL, requestId, CXP_ROUTE_TOKEN_ESIGN_SIGN],
      recipientId == null ? {} : { queryParams: { [RECIPIENT_ID_QUERY_PARAM]: recipientId } },
    );
  }

  /**
   * Opens the signed copy in the preview dialog the rest of the portal uses for documents, rather
   * than handing the browser a presigned URL in a new tab — which left the signer outside the
   * portal, looking at a bare PDF with a link that expires.
   */
  protected onViewDocument(): void {
    const detail = this.esign();
    if (!detail?.signedFileUrl) return;
    this.filePreview.previewByUrl({ name: detail.filename ?? DEFAULT_SIGNED_FILENAME }, detail.signedFileUrl);
  }

  /**
   * A failed eSign read leaves the header and rail intact: the request loaded,
   * only its bundle did not, and a signer who lands here still benefits from
   * seeing which request they are looking at.
   */
  private loadEsignDetail(requestId: number): void {
    this.esignApi
      .getDetailForClientRequest(requestId, this.recipientId())
      .pipe(
        catchError((err) => {
          this.logger.error('EsignDetailContainer :: failed to load eSign detail', err);
          this.esignError.set(clientFacingMessage(err));
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((detail) => {
        this.esign.set(detail);
        this.esignUnavailable.set(detail === null);
      });
  }
}
